import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  completeTask,
  createTask,
  deleteTask,
  listContactTasks,
  listTasks,
  runFollowUp,
  type CreateTaskParams,
} from '@/api/tasks'

export function useTasks(status: 'open' | 'done' = 'open') {
  return useQuery({ queryKey: ['tasks', status], queryFn: () => listTasks(status) })
}

export function useCreateTask(dealId: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (params: Omit<CreateTaskParams, 'dealId'>) => createTask({ ...params, dealId }),
    onSuccess: () => {
      // the ['tasks'] prefix also covers ['tasks', 'deal', dealId] used by the deal panel
      qc.invalidateQueries({ queryKey: ['tasks'] })
      qc.invalidateQueries({ queryKey: ['dashboard'] }) // overdue tasks, at-risk deals
      qc.invalidateQueries({ queryKey: ['attention'] }) // a task moves a deal's Focus score
      // A task's due date becomes the deal's next contact date server-side — refresh the deal views.
      qc.invalidateQueries({ queryKey: ['deal', dealId] })
      qc.invalidateQueries({ queryKey: ['deals', 'board'] })
    },
  })
}

/** Tasks about one contact (open and done, newest first) — the contact's Tasks card. */
export function useContactTasks(contactId?: string) {
  return useQuery({
    queryKey: ['tasks', 'contact', contactId],
    queryFn: () => listContactTasks(contactId as string),
    enabled: !!contactId,
  })
}

/** A task from the New task dialog — on a contact, a deal or on its own. */
export function useAddTask() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (params: CreateTaskParams) => createTask(params),
    onSuccess: (task) => {
      qc.invalidateQueries({ queryKey: ['tasks'] }) // covers the deal's and the contact's task lists
      qc.invalidateQueries({ queryKey: ['dashboard'] })
      qc.invalidateQueries({ queryKey: ['attention'] })
      if (task.contactId) qc.invalidateQueries({ queryKey: ['contact-activities', task.contactId] })
      if (task.dealId) {
        qc.invalidateQueries({ queryKey: ['deal', task.dealId] })
        qc.invalidateQueries({ queryKey: ['activities', task.dealId] })
        qc.invalidateQueries({ queryKey: ['deals', 'board'] })
      }
    },
  })
}

export function useCompleteTask() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id: string) => completeTask(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['tasks'] })
      qc.invalidateQueries({ queryKey: ['dashboard'] })
      qc.invalidateQueries({ queryKey: ['attention'] }) // a task moves a deal's Focus score
      // the deal's next contact date follows its open tasks — the next one (or none) takes over
      qc.invalidateQueries({ queryKey: ['deal'] })
      qc.invalidateQueries({ queryKey: ['deals', 'board'] })
    },
  })
}

export function useDeleteTask() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id: string) => deleteTask(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['tasks'] })
      qc.invalidateQueries({ queryKey: ['dashboard'] })
      qc.invalidateQueries({ queryKey: ['attention'] }) // a task moves a deal's Focus score
      // the deal's next contact date follows its open tasks — the next one (or none) takes over
      qc.invalidateQueries({ queryKey: ['deal'] })
      qc.invalidateQueries({ queryKey: ['deals', 'board'] })
    },
  })
}

export function useRunFollowUp() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: () => runFollowUp(),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['tasks'] })
      qc.invalidateQueries({ queryKey: ['dashboard'] })
      qc.invalidateQueries({ queryKey: ['attention'] }) // a task moves a deal's Focus score
    },
  })
}
